import React, { useState } from 'react';
import { useAuthStore, useUIStore } from '../stores';
import {
  User,
  Mail,
  Lock,
  Bell,
  Globe,
  Palette,
  Shield,
  Smartphone,
  Download,
  Trash2,
  Save,
  Loader2,
  Check,
  Moon,
  Sun,
  Monitor,
} from 'lucide-react';

export default function Settings() {
  const { user, updateUser } = useAuthStore();
  const { theme, setTheme, addToast } = useUIStore();
  
  const [activeTab, setActiveTab] = useState('profile');
  const [isSaving, setIsSaving] = useState(false);
  
  // Profile form state
  const [name, setName] = useState(user?.full_name || user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  
  // Notification preferences
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    postPublished: true,
    weeklyReport: true,
    productUpdates: false,
  });
  
  const handleSaveProfile = async () => {
    setIsSaving(true);
    const result = await updateUser({ full_name: name, email });
    setIsSaving(false);
    if (result.success) {
      addToast({ type: 'success', title: 'Settings saved', message: 'Your profile has been updated.' });
    } else {
      addToast({ type: 'error', title: 'Update failed', message: result.error || 'Could not save profile.' });
    }
  };
  
  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'data', label: 'Data & Privacy', icon: Download },
  ];
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Manage your account preferences
        </p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="card p-2">
          <nav className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
        
        {/* Content */}
        <div className="lg:col-span-3 card p-6">
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Profile Settings</h2>
                <p className="text-sm text-gray-500 mt-1">Update your personal information</p>
              </div>
              
              {/* Avatar */}
              <div className="flex items-center gap-4">
                <img 
                  src={user?.avatar} 
                  alt={user?.name}
                  className="w-16 h-16 rounded-full"
                />
                <div>
                  <button className="btn-secondary text-sm">Change Avatar</button>
                  <p className="text-xs text-gray-500 mt-1">JPG, PNG or GIF. Max 2MB.</p>
                </div>
              </div>
              
              {/* Form */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label block mb-1.5">Full name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input"
                  />
                </div>
                <div>
                  <label className="label block mb-1.5">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input"
                  />
                </div>
              </div>
              
              <div>
                <label className="label block mb-1.5">Timezone</label>
                <select className="input">
                  <option>America/Toronto (EST)</option>
                  <option>America/New_York (EST)</option>
                  <option>America/Los_Angeles (PST)</option>
                  <option>Europe/London (GMT)</option>
                  <option>Asia/Tokyo (JST)</option>
                </select>
              </div>
              
              <button 
                onClick={handleSaveProfile}
                disabled={isSaving}
                className="btn-primary"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </button>
            </div>
          )}
          
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Notification Preferences</h2>
                <p className="text-sm text-gray-500 mt-1">Choose how you want to be notified</p>
              </div>
              
              <div className="space-y-4">
                {[
                  { id: 'email', label: 'Email notifications', desc: 'Receive notifications via email' },
                  { id: 'push', label: 'Push notifications', desc: 'Receive browser push notifications' },
                  { id: 'postPublished', label: 'Post published', desc: 'When your scheduled posts go live' },
                  { id: 'weeklyReport', label: 'Weekly report', desc: 'Weekly performance summary' },
                  { id: 'productUpdates', label: 'Product updates', desc: 'News about new features' },
                ].map((item) => (
                  <label key={item.id} className="flex items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-gray-800 cursor-pointer">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{item.label}</p>
                      <p className="text-sm text-gray-500">{item.desc}</p>
                    </div>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={notifications[item.id]}
                        onChange={(e) => setNotifications(prev => ({ ...prev, [item.id]: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 dark:bg-gray-600 rounded-full peer peer-checked:bg-brand-500 transition-colors" />
                      <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow peer-checked:translate-x-5 transition-transform" />
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
          
          {activeTab === 'appearance' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Appearance</h2>
                <p className="text-sm text-gray-500 mt-1">Customize how the app looks</p>
              </div>
              
              <div>
                <label className="label block mb-3">Theme</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { id: 'light', label: 'Light', icon: Sun },
                    { id: 'dark', label: 'Dark', icon: Moon },
                    { id: 'system', label: 'System', icon: Monitor },
                  ].map((option) => (
                    <button
                      key={option.id}
                      onClick={() => setTheme(option.id)}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                        theme === option.id
                          ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <option.icon className={`w-6 h-6 ${theme === option.id ? 'text-brand-500' : 'text-gray-400'}`} />
                      <span className={`text-sm font-medium ${theme === option.id ? 'text-brand-600' : 'text-gray-600 dark:text-gray-400'}`}>
                        {option.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'security' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Security</h2>
                <p className="text-sm text-gray-500 mt-1">Manage your account security</p>
              </div>
              
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Lock className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">Password</p>
                        <p className="text-sm text-gray-500">Last changed 30 days ago</p>
                      </div>
                    </div>
                    <button className="btn-secondary text-sm">Change</button>
                  </div>
                </div>
                
                <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Smartphone className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">Two-factor authentication</p>
                        <p className="text-sm text-gray-500">Add an extra layer of security</p>
                      </div>
                    </div>
                    <button className="btn-secondary text-sm">Enable</button>
                  </div>
                </div>
                
                <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Globe className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">Active sessions</p>
                        <p className="text-sm text-gray-500">Manage devices logged into your account</p>
                      </div>
                    </div>
                    <button className="btn-secondary text-sm">View All</button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'data' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Data & Privacy</h2>
                <p className="text-sm text-gray-500 mt-1">Manage your data and account</p>
              </div>
              
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Download className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">Export your data</p>
                        <p className="text-sm text-gray-500">Download a copy of your data</p>
                      </div>
                    </div>
                    <button className="btn-secondary text-sm">Export</button>
                  </div>
                </div>
                
                <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Trash2 className="w-5 h-5 text-red-500" />
                      <div>
                        <p className="font-medium text-red-700 dark:text-red-400">Delete account</p>
                        <p className="text-sm text-red-600 dark:text-red-500">Permanently delete your account and data</p>
                      </div>
                    </div>
                    <button className="btn-danger text-sm">Delete</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
